import { Layer, ThemeContext } from 'grommet';

import CloseButton from '@/components/modals/CloseButton';
import VideoPlayer from '@/components/VideoPlayer';

interface TourVideoModalProps {
  src: string;
  onClose: () => void;
}

const layerTheme = {
  layer: {
    background: 'transparent',
    border: { radius: '16px' },
    // No `overflow: hidden` on the container: the close button sits OUTSIDE the video box (at
    // the screen corner), and clipping the container would hide it. The video's own rounded
    // corners come from the inner wrapper instead.
  },
};

/**
 * The "Show me how" demo. Reuses the app's `VideoPlayer` (the same one ShareModal plays UGC
 * with) so playback, controls and buffering behave exactly as they do everywhere else.
 *
 * Closing it and playing it to the end are the same transition — the user is done with the video
 * either way — so both call `onClose`, and the step's config decides where that goes.
 *
 * `noOverlay` suppresses the player's follow / visit-habitat end screen (this is a demo, not a
 * piece of content to convert on), and `logView` / `stopAnalytics` keep a tour demo out of the
 * video-view analytics.
 */
const TourVideoModal = ({ src, onClose }: TourVideoModalProps) => (
  <ThemeContext.Extend value={layerTheme}>
    <Layer animation="fadeIn" responsive={false} onClickOutside={onClose} onEsc={onClose}>
      {/*
        Pinned to the screen corner, outside the player — the same placement, and the same trick,
        as ShareModal. A plain `fixed` corner would drift, because Grommet's Layer centres its
        contents with a transform; instead a zero-width, full-height column is anchored at the
        horizontal centre and pushed 50vw right to the screen edge, and the button sits in its
        top-right.
      */}
      <div className="preflight pointer-events-none absolute right-1/2 top-1/2 z-[22] h-screen w-0 -translate-y-1/2 translate-x-[50vw]">
        <CloseButton onClick={onClose} className="pointer-events-auto absolute right-6 top-6 text-white" />
      </div>

      <div className="preflight w-[900px] max-w-[90vw] overflow-hidden bg-black">
        {/* A stable synthetic id: the player wants one, but analytics/view-logging are off. */}
        <VideoPlayer
          videoURL={src}
          videoId="tour-demo"
          autoPlay
          controls
          noOverlay
          logView={false}
          stopAnalytics
          onEnd={onClose}
        />
      </div>
    </Layer>
  </ThemeContext.Extend>
);

export default TourVideoModal;
